import React, { useEffect, useState } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import { format } from 'date-fns';
import AssignmentIcon from '@mui/icons-material/Assignment';

// Define keyframes for the spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface ExecutiveSummarySectionProps {
  summary: string;
  summaryDate: string;
  onRegenerate: () => void;
  isGenerating: boolean;
  lastSyncDate?: string;
}

const ExecutiveSummarySection: React.FC<ExecutiveSummarySectionProps> = ({ 
  summary, 
  summaryDate, 
  onRegenerate, 
  isGenerating,
  lastSyncDate 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add the keyframes to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(spinnerKeyframes));
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Truncate summary for mobile view
  const getTruncatedSummary = (text: string, maxLines: number = 3) => {
    const words = text.split(' ');
    const wordsPerLine = 12; // Approximate words per line on mobile
    const maxWords = maxLines * wordsPerLine;
    
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  if (!summary) {
    return null;
  }

  return (
    <div style={{
      //background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      background: 'linear-gradient(90deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
      color: '#fff',
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '1rem'
      }}>
        <div>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '700', 
            marginBottom: '0.5rem',
            color: '#fff'
          }}>
            <AssignmentIcon style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Executive Summary
          </h2>
          <p style={{ 
            color: '#e2e8f0', 
            fontSize: '0.9rem',
            margin: '0'
          }}>
            Generated on {format(summaryDate, 'do MMMM yyyy, h:mm a')}
          </p>          
        </div>
        {lastSyncDate && new Date(lastSyncDate) > new Date(summaryDate) && (
          <button
            onClick={onRegenerate}
            disabled={isGenerating}
            style={{
              background: isGenerating ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isGenerating) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isGenerating) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              }
            }}
          >
            {isGenerating ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Generating...
              </>
            ) : (
              'Regenerate'
            )}
          </button>
        )}
      </div>
      <div style={{
        maxHeight: isMobile && !isExpanded ? '120px' : 'none',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out'
      }}>
        <MarkdownRenderer content={isMobile && !isExpanded ? getTruncatedSummary(summary) : summary} />
      </div>
      
      {isMobile && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            marginTop: '1rem',
            width: '100%',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
          }}
        >
          {isExpanded ? (
            <>
              Show Less
              <span style={{ transform: 'rotate(180deg)', transition: 'transform 0.2s ease' }}>▼</span>
            </>
          ) : (
            <>
              Show More
              <span style={{ transition: 'transform 0.2s ease' }}>▼</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ExecutiveSummarySection;
